import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { DotLoading, Toast } from 'antd-mobile';
import type {
  DrawTimeConfig,
  GameType,
  LatestDrawResponse,
  LotterySiteSettings,
  PlayType,
  ZiliaoGroup,
  ZiliaoRecord,
} from '../types/lottery';
import { GAMES } from '../types/lottery';
import {
  getDrawTimeConfig,
  getLatestDraw,
  getLotterySiteSettings,
  getPlayTypes,
  getZiliaoAll,
} from '../api/lottery';
import { formatPeriod, shortPeriod, splitDrawBalls } from '../utils/ball';
import { applySiteMeta } from '../utils/siteMeta';
import { readCachedSiteSettings, resolveSiteName, resolveSiteShortName, writeCachedSiteSettings } from '../utils/siteSettingsCache';
import { resolveUploadUrl } from '../utils/uploadUrl';
import LotteryBall from '../components/LotteryBall';
import AdCarousel from '../components/AdCarousel';
import ImageBindingSceneCard from '../components/ImageBindingSceneCard';
import PlayRuleLegend from '../components/PlayRuleLegend';
import './Home.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIMEZONE = 'Asia/Shanghai';
const DEFAULT_GAME: GameType = 'amssc';
export type HitEffectMode = PlayType['hit_effect_mode'];
type GroupedValueStyle = {
  text: string;
  accent: string;
  isGradient?: boolean;
};

export function resolveGameParam(value: null | string): GameType {
  return GAMES.some((item) => item.code === value) ? (value as GameType) : DEFAULT_GAME;
}

export function resolveLotteryTimezone(config: DrawTimeConfig | null): string {
  const timezoneName = config?.timezone?.trim();
  if (!timezoneName) {
    return DEFAULT_TIMEZONE;
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezoneName });
    return timezoneName;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

export function buildTimezoneAwareTime(unixSeconds?: number, timezoneName?: string) {
  if (!unixSeconds) {
    return null;
  }
  return dayjs.unix(unixSeconds).tz(timezoneName || DEFAULT_TIMEZONE);
}

function escapeRegExp(source: string) {
  return source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function compactPlayTitle(title: string, game: GameType) {
  const source = String(title || '').trim();
  if (!source) {
    return '--';
  }

  const currentGame = GAMES.find((item) => item.code === game);
  const candidates = [
    currentGame?.name,
    currentGame?.shortName,
    currentGame?.name.replace(/^澳门/, '澳'),
    currentGame?.name.replace(/^香港/, '港'),
  ]
    .filter((item): item is string => Boolean(item))
    .sort((a, b) => b.length - a.length);

  let result = source;
  candidates.forEach((candidate) => {
    result = result.replace(
      new RegExp(`^[【\\[]?${escapeRegExp(candidate)}[】\\]]?(?:\\s*[·•|｜/\\\\\\-_:：]\\s*|\\s+)?`, 'u'),
      '',
    );
  });

  result = result.replace(/^[·•|｜/\\\-_:：\s]+/, '').trim();
  return result || source;
}

const allSx = ['鼠', '牛', '虎', '兔', '龙', '蛇', '马', '羊', '猴', '鸡', '狗', '猪'];
const extraGroupPlayTags = new Set([
  'special_family',
  'special_front_back',
  'special_sky_ground',
  'special_yin_yang',
  'all7_yin_yang',
  'special_male_female',
  'special_good_bad',
]);
const dsWords = ['大', '小', '单', '双', '日', '夜', '文', '武', '男', '女', '前', '后', '左', '右'];
const boWords = ['红', '蓝', '绿', '红波', '蓝波', '绿波'];
const wxWords = ['金', '木', '水', '火', '土'];
const cultureWords = ['春', '夏', '秋', '冬', '东', '南', '西', '北', '琴', '棋', '书', '画', '风', '雷', '云', '雨'];
const prefixWords = ['合', '尾', '头'];
const neutralWords = ['肖', '波', '段', '门', '期', '特', '平'];
const compoundTokens = ['红波', '蓝波', '绿波'];

const boColor: Record<string, string> = {
  红: '#e74c3c',
  红波: '#e74c3c',
  蓝: '#2980b9',
  蓝波: '#2980b9',
  绿: '#27ae60',
  绿波: '#27ae60',
};
const wxColor: Record<string, string> = { 金: '#d4a017', 木: '#27ae60', 水: '#2980b9', 火: '#e74c3c', 土: '#8b6914' };
const dsColor: Record<string, string> = {
  大: '#e74c3c',
  小: '#2980b9',
  单: '#f39c12',
  双: '#2980b9',
  日: '#e67e22',
  夜: '#8e44ad',
  文: '#16a085',
  武: '#c0392b',
  男: '#2980b9',
  女: '#e91e63',
  前: '#e74c3c',
  后: '#2980b9',
  左: '#e74c3c',
  右: '#2980b9',
};
const prefixColor: Record<string, string> = { 头: '#e74c3c', 尾: '#2980b9', 合: '#8e44ad' };
const groupedSuffixColor: Record<string, string> = {
  头: '#e74c3c',
  尾: '#2980b9',
  合: '#8e44ad',
  门: '#16a085',
  段: '#d4a017',
  期: '#7f8c8d',
  波: '#27ae60',
  肖: '#8b5e3c',
};
const cultureColor: Record<string, string> = {
  春: '#27ae60',
  夏: '#e74c3c',
  秋: '#d4a017',
  冬: '#2980b9',
  东: '#27ae60',
  南: '#e74c3c',
  西: '#d4a017',
  北: '#2980b9',
  琴: '#8e44ad',
  棋: '#16a085',
  书: '#2980b9',
  画: '#e74c3c',
  风: '#27ae60',
  雷: '#e74c3c',
  云: '#d4a017',
  雨: '#2980b9',
};

export function normalizePlayTag(tag?: string) {
  return (tag || '').replace(/s$/, '');
}

function normalizeSectionLabel(label: string) {
  return label === '预测单双' ? '单双' : label;
}

function getSectionLabel(section: string) {
  const index = section.indexOf(':');
  const label = index >= 0 ? section.slice(0, index).trim() : section.trim();
  return normalizeSectionLabel(label);
}

function pickSections(sections: string[], labels: string[]) {
  const labelSet = new Set(labels);
  return sections.filter((section) => labelSet.has(getSectionLabel(section)));
}

function simplifySections(sections: string[], playTag?: string) {
  switch (normalizePlayTag(playTag)) {
    case 'zhufangbo':
      return pickSections(sections, ['主', '防', '四肖', '六尾', '单双']);
    case 'jinritoutiao':
      return pickSections(sections, ['平特肖', '特头', '特尾', '五行', '波色', '特肖']);
    case 'paomajindi':
      return pickSections(sections, ['禁4码', '二肖', '头', '尾', '波', '段']);
    case 'jiuxiaozhongte':
      return pickSections(sections, ['3肖', '6肖', '9肖']);
    default:
      return sections;
  }
}

function buildSectionRows(sections: string[], playTag?: string) {
  const normalizedTag = normalizePlayTag(playTag);
  const rowGroups = [
    ['主', '防'],
    ['头', '尾'],
    ['特头', '特尾'],
  ];
  const rows: string[][] = [];
  const used = new Set<number>();

  if (normalizedTag === 'zhufangbo' || normalizedTag === 'facaiboju') {
    rowGroups.forEach((labels) => {
      const matched = sections
        .map((section, index) => ({ section, index }))
        .filter(({ section, index }) => !used.has(index) && labels.includes(getSectionLabel(section)));
      if (matched.length > 0) {
        matched.forEach(({ index }) => used.add(index));
        rows.push(matched.map(({ section }) => section));
      }
    });
    sections.forEach((section, index) => {
      if (!used.has(index)) {
        rows.push([section]);
      }
    });
    return rows;
  }

  rowGroups.forEach((labels) => {
    const matched = sections
      .map((section, index) => ({ section, index }))
      .filter(({ section, index }) => !used.has(index) && labels.includes(getSectionLabel(section)));
    if (matched.length > 1) {
      matched.forEach(({ index }) => used.add(index));
      rows.push(matched.map(({ section }) => section));
    }
  });

  sections.forEach((section, index) => {
    if (!used.has(index)) {
      rows.push([section]);
    }
  });

  return rows;
}

function sortRowsByLengthAsc(rows: string[][]) {
  return rows
    .map((row, index) => ({
      row,
      index,
      length: row.join(' ').replace(/\s+/g, ' ').trim().length,
    }))
    .sort((a, b) => a.length - b.length || a.index - b.index)
    .map((item) => item.row);
}

function getSingleValueColor(value: string) {
  if (allSx.includes(value)) {
    return '#8b5e3c';
  }
  if (boWords.includes(value)) {
    return boColor[value] || '#999';
  }
  if (wxWords.includes(value)) {
    return wxColor[value] || '#999';
  }
  if (dsWords.includes(value)) {
    return dsColor[value] || '#999';
  }
  if (prefixWords.includes(value)) {
    return prefixColor[value] || '#999';
  }
  if (cultureWords.includes(value)) {
    return cultureColor[value] || '#999';
  }
  return null;
}

function getGroupedValueStyle(value: string): GroupedValueStyle | null {
  const source = String(value || '').trim();
  if (!source) {
    return null;
  }
  if (boWords.includes(source)) {
    return null;
  }

  const match = source.match(/^(\d{1,2})(头|尾|合|门|段|期|波|肖)$/);
  if (match) {
    return {
      text: `${match[1]}${match[2]}`,
      accent: groupedSuffixColor[match[2]] || '#999',
    };
  }

  const semanticSuffixMatch = source.match(/^([\u4e00-\u9fa5A-Za-z]{1,3})(头|尾|合|门|段|期|波|肖)$/u);
  if (semanticSuffixMatch) {
    return {
      text: source,
      accent: groupedSuffixColor[semanticSuffixMatch[2]] || '#999',
    };
  }

  if (source.length === 2) {
    const first = source[0] || '';
    const second = source[1] || '';
    const firstColor = getSingleValueColor(first);
    const secondColor = getSingleValueColor(second);
    if (firstColor && secondColor) {
      return {
        text: source,
        accent: `linear-gradient(135deg, ${firstColor} 0%, ${firstColor} 48%, ${secondColor} 52%, ${secondColor} 100%)`,
        isGradient: true,
      };
    }
  }

  return null;
}

function renderStyledValue(value: string, hlClass: string) {
  const groupedValue = getGroupedValueStyle(value);
  if (groupedValue) {
    const groupedStyle = groupedValue.isGradient
      ? ({
          backgroundImage: groupedValue.accent,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          WebkitTextFillColor: 'transparent',
        } as React.CSSProperties)
      : ({ color: groupedValue.accent } as React.CSSProperties);

    return (
      <span className={`zt-tag${groupedValue.isGradient ? ' zt-tag-gradient' : ''}${hlClass}`} style={groupedStyle}>
        {groupedValue.text}
      </span>
    );
  }
  if (/^\d{1,2}$/.test(value)) {
    return <span className={`zt-num-tag${hlClass}`}>{value}</span>;
  }
  const singleValueColor = getSingleValueColor(value);
  if (singleValueColor) {
    return (
      <span className={`zt-tag${hlClass}`} style={{ color: singleValueColor }}>
        {value}
      </span>
    );
  }
  return <span className={`zt-text-tag${hlClass}`}>{value}</span>;
}

function splitCompoundParts(value: string) {
  const source = String(value || '').trim();
  if (!source) {
    return [];
  }
  const parts: string[] = [];
  let index = 0;
  while (index < source.length) {
    const rest = source.slice(index);
    const compoundMatch = compoundTokens.find((token) => rest.startsWith(token));
    if (compoundMatch) {
      parts.push(compoundMatch);
      index += compoundMatch.length;
      continue;
    }
    const char = rest[0] || '';
    if (
      dsWords.includes(char)
      || prefixWords.includes(char)
      || neutralWords.includes(char)
      || cultureWords.includes(char)
      || wxWords.includes(char)
      || allSx.includes(char)
    ) {
      parts.push(char);
      index += 1;
      continue;
    }
    let plain = char;
    index += 1;
    while (index < source.length) {
      const nextRest = source.slice(index);
      const nextCompoundMatch = compoundTokens.find((token) => nextRest.startsWith(token));
      const nextChar = source[index] || '';
      if (
        nextCompoundMatch
        || dsWords.includes(nextChar)
        || prefixWords.includes(nextChar)
        || neutralWords.includes(nextChar)
        || cultureWords.includes(nextChar)
        || wxWords.includes(nextChar)
        || allSx.includes(nextChar)
      ) {
        break;
      }
      plain += nextChar;
      index += 1;
    }
    parts.push(plain);
  }
  return parts.filter(Boolean);
}

function renderCompoundValue(value: string, hlClass: string) {
  const clusterHlClass = hlClass.includes('zt-hit-legacy') ? '' : hlClass;
  if (getGroupedValueStyle(value)) {
    return renderStyledValue(value, hlClass);
  }
  const parts = splitCompoundParts(value);
  if (parts.length <= 1) {
    return renderStyledValue(value, hlClass);
  }
  return (
    <span className={`zt-tag-cluster${clusterHlClass}`}>
      {parts.map((part, index) => {
        if (neutralWords.includes(part)) {
          return (
            <span key={`${part}-${index}`} className={`zt-neutral-tag${hlClass}`}>
              {part}
            </span>
          );
        }
        return <React.Fragment key={`${part}-${index}`}>{renderStyledValue(part, hlClass)}</React.Fragment>;
      })}
    </span>
  );
}

function parseOpenTitleValues(openTitle?: string) {
  return String(openTitle || '')
    .split(/[/|\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function resolveEffectValues(effectValues?: string[], openTitle?: string) {
  if (Array.isArray(effectValues) && effectValues.length > 0) {
    return effectValues.map((item) => item.trim()).filter(Boolean);
  }
  return parseOpenTitleValues(openTitle);
}

function resolveHitClassName(isHit: boolean, hitEffectMode?: HitEffectMode) {
  if (!isHit) {
    return '';
  }
  return hitEffectMode === 'legacy' ? ' zt-hit zt-hit-current zt-hit-legacy' : ' zt-hit zt-hit-current';
}

function normalizeExtraGroupSection(section: string, playTag?: string) {
  const normalizedTag = String(playTag || '').trim();
  if (!extraGroupPlayTags.has(normalizedTag)) {
    return section;
  }

  const trimmed = String(section || '').trim();
  if (!trimmed || trimmed.includes('+') || trimmed.includes(':')) {
    return trimmed;
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return trimmed;
  }
  if (allSx.includes(parts[0])) {
    return trimmed;
  }
  if (!parts.slice(1).every((part) => allSx.includes(part))) {
    return trimmed;
  }
  return `${parts[0]} + ${parts.slice(1).join(' ')}`;
}

function renderToken(
  token: string,
  key: number,
  hitValues: string[],
  hitEffectMode?: HitEffectMode,
) {
  const source = token.trim();
  if (!source) {
    return null;
  }

  const hasLeftBracket = source.startsWith('【');
  const hasRightBracket = source.endsWith('】');
  const trimmed = source.replace(/^【|】$/g, '').trim();
  if (!trimmed) {
    return null;
  }

  const hitSet = new Set(hitValues);
  const isHit = hitSet.has(trimmed);
  const hlClass = resolveHitClassName(isHit, hitEffectMode);

  let content: React.ReactNode;
  if (trimmed.includes(':')) {
    const [label, ...rest] = trimmed.split(':');
    const restText = rest.join(':').trim();
    const displayLabel = normalizeSectionLabel(label.trim());
    content = (
      <span className="zt-group">
        <span className="zt-label">{displayLabel}:</span>
        <span className="zt-values">
          {restText ? renderInlineValues(restText, key, hitValues, hitEffectMode) : null}
        </span>
      </span>
    );
  } else {
    content = renderCompoundValue(trimmed, hlClass);
  }

  return (
    <React.Fragment key={key}>
      {hasLeftBracket && <span className="zt-punc">【</span>}
      {content}
      {hasRightBracket && <span className="zt-punc">】</span>}
    </React.Fragment>
  );
}

function renderInlineValues(
  value: string,
  keyBase: number,
  hitValues: string[],
  hitEffectMode?: HitEffectMode,
) {
  const tokens = value.split(/(\+|\s+)/).filter(Boolean);
  return tokens.map((token, index) => {
    const key = keyBase * 100 + index;
    if (/^\s+$/.test(token)) {
      return (
        <span key={key} className="zt-sep zt-sep-space">
          {' '}
        </span>
      );
    }
    if (token === '+') {
      const prev = tokens[index - 1]?.trim();
      const next = tokens[index + 1]?.trim();
      if (prev && next && allSx.includes(prev) && allSx.includes(next)) {
        return null;
      }
      return (
        <span key={key} className="zt-sep zt-sep-plus">
          {token}
        </span>
      );
    }
    return renderToken(token, key, hitValues, hitEffectMode);
  });
}

function renderSection(
  section: string,
  keyBase: number,
  hitValues: string[],
  hitEffectMode?: HitEffectMode,
) {
  const trimmed = section.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes(':')) {
    return renderToken(trimmed, keyBase, hitValues, hitEffectMode);
  }

  const tokens = trimmed.split(/(\+|\s+)/).filter(Boolean);
  return (
    <>
      {tokens.map((token, tokenIndex) => {
        const key = keyBase + tokenIndex;
        if (/^\s+$/.test(token)) {
          return (
            <span key={key} className="zt-sep zt-sep-space">
              {' '}
            </span>
          );
        }
        if (token === '+') {
          return (
            <span key={key} className="zt-sep zt-sep-plus">
              {token}
            </span>
          );
        }
        return renderToken(token, key, hitValues, hitEffectMode);
      })}
    </>
  );
}

export function renderPredictionTitle(
  title?: string,
  playTag?: string,
  openTitle?: string,
  effectValues?: string[],
  hitEffectMode?: HitEffectMode,
) {
  const text = String(title || '').trim();
  if (!text || text === '--') {
    return <span className="zt-empty">暂无预测</span>;
  }
  const hitValues = resolveEffectValues(effectValues, openTitle);
  const sections = simplifySections(
    text
      .split('|')
      .map((section) => normalizeExtraGroupSection(section, playTag))
      .filter(Boolean),
    playTag,
  );
  const rows = sortRowsByLengthAsc(buildSectionRows(sections, playTag));

  return (
    <div className="zt-content-block">
      {rows.map((rowSections, rowIndex) => {
        const isInlinePair = rowSections.length > 1;
        return (
          <div key={rowIndex} className={`zt-content-line${isInlinePair ? ' zt-content-line-inline' : ''}`}>
            {rowSections.map((section, sectionIndex) => {
              const sectionKey = rowIndex * 1000 + sectionIndex * 100;
              const content = renderSection(section, sectionKey, hitValues, hitEffectMode);
              return (
                <React.Fragment key={sectionKey}>
                  {isInlinePair ? (
                    <>
                      {sectionIndex > 0 && <span className="zt-inline-divider" />}
                      <div className="zt-inline-segment">{content}</div>
                    </>
                  ) : (
                    <>
                      {sectionIndex > 0 && <span className="zt-sep zt-sep-gap"> </span>}
                      {content}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export function hasVisualItems(record: ZiliaoRecord) {
  return Boolean(record.visual_payload && Array.isArray(record.visual_payload.items) && record.visual_payload.items.length > 0);
}

export function isPendingRecord(record: ZiliaoRecord) {
  return record.is_opened === false || (record.suc !== true && record.suc !== false && !record.open_title);
}

export function openResultText(record: ZiliaoRecord) {
  return record.open_title || '待开奖';
}

export function renderJudge(record: ZiliaoRecord) {
  if (record.suc === true) {
    return <span className="record-judge is-hit">准</span>;
  }
  if (record.suc === false) {
    return <span className="record-judge is-miss">错</span>;
  }
  return <span className="record-judge is-pending">待开奖</span>;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const game = resolveGameParam(searchParams.get('game'));
  const [loading, setLoading] = useState(true);
  const [latest, setLatest] = useState<LatestDrawResponse | null>(null);
  const [drawTimeConfig, setDrawTimeConfig] = useState<DrawTimeConfig | null>(null);
  const [playTypes, setPlayTypes] = useState<PlayType[]>([]);
  const [groups, setGroups] = useState<ZiliaoGroup[]>([]);
  const [siteSettings, setSiteSettings] = useState<LotterySiteSettings | null>(() => readCachedSiteSettings());
  const [selectedTag, setSelectedTag] = useState('');
  const [countdown, setCountdown] = useState('');
  const requestRef = useRef(0);
  const latestRequestRef = useRef(0);
  const currentGameRef = useRef(game);
  const siteSettingsRequestRef = useRef(0);
  currentGameRef.current = game;

  const fetchData = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const [latestData, drawTimeData, playTypeData, groupData] = await Promise.all([
        getLatestDraw(game),
        getDrawTimeConfig(game),
        getPlayTypes(game),
        getZiliaoAll(game, 12),
      ]);
      if (requestId !== requestRef.current) {
        return;
      }
      setDrawTimeConfig(drawTimeData);
      setPlayTypes(playTypeData);
      setGroups(groupData);
      // 首次加载直接显示当前最新一期，不播动画
      setLatest(latestData);
    } catch {
      if (requestId !== requestRef.current) {
        return;
      }
      Toast.show({ content: '加载失败', icon: 'fail' });
      setLatest(null);
      setDrawTimeConfig(null);
      setPlayTypes([]);
      setGroups([]);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [game]);

  const refreshLatestDraw = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    try {
      const latestData = await getLatestDraw(game);
      if (requestId !== latestRequestRef.current || currentGameRef.current !== game) {
        return;
      }
      const phase = drawAnimationPhaseRef.current;

      // revealing 阶段：揭示中，丢弃所有 polling 数据
      if (phase === 'revealing') {
        return;
      }

      // syncing 阶段：持续刷新暂存，看号码是否到位、是否可推进 revealing
      if (phase === 'syncing') {
        pendingLatestRef.current = latestData;
        tryEnterRevealingRef.current();
        return;
      }

      // idle 阶段
      // awaiting_result：动画播完了，等后端开奖结算 → is_drawing=false 才刷新一次
      if (drawCycleRef.current === 'awaiting_result') {
        if (!latestData.is_drawing) {
          drawCycleRef.current = 'normal';
          setLatest(latestData);
        }
        // 还在 is_drawing=true 的 60s 窗口里，不做任何事
        return;
      }

      // normal：检测 is_drawing 翻转 true → 进入 syncing
      if (latestData.is_drawing) {
        const baselinePeriod = String(latest?.prev?.period || '');
        pendingLatestRef.current = latestData;
        enterSyncingRef.current(baselinePeriod);
        // 立刻尝试推进一次（如果号码已经到位且基线不同，且 10s 满足，会直接进 revealing）
        tryEnterRevealingRef.current();
        return;
      }

      // 常规刷新
      setLatest(latestData);
    } catch {
      // ignore
    }
  }, [game, latest?.prev?.period]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // 切彩种时重置开奖周期状态
  useEffect(() => {
    drawCycleRef.current = 'normal';
    animateBaselinePeriodRef.current = '';
    syncingStartedAtRef.current = 0;
    pendingLatestRef.current = null;
  }, [game]);

  useEffect(() => {
    const rawGame = searchParams.get('game');
    if (rawGame !== game) {
      setSearchParams({ game }, { replace: true });
    }
  }, [game, searchParams, setSearchParams]);

  useEffect(() => {
    const requestId = ++siteSettingsRequestRef.current;
    getLotterySiteSettings()
      .then((res) => {
        if (requestId !== siteSettingsRequestRef.current) {
          return;
        }
        setSiteSettings(writeCachedSiteSettings(res));
      })
      .catch(() => {
        if (requestId !== siteSettingsRequestRef.current) {
          return;
        }
      });
  }, []);

  const orderedGroups = useMemo(() => {
    const groupMap = new Map(groups.map((group) => [group.tag, group]));
    const ordered = playTypes
      .map((item) => groupMap.get(item.tag))
      .filter((item): item is ZiliaoGroup => Boolean(item));

    if (ordered.length > 0) {
      return ordered;
    }
    return groups;
  }, [groups, playTypes]);

  const playHitEffectModeByTag = useMemo(
    () =>
      playTypes.reduce<Record<string, HitEffectMode>>((acc, item) => {
        acc[item.tag] = item.hit_effect_mode;
        const normalizedTag = normalizePlayTag(item.tag);
        if (!acc[normalizedTag]) {
          acc[normalizedTag] = item.hit_effect_mode;
        }
        return acc;
      }, {}),
    [playTypes],
  );

  useEffect(() => {
    if (orderedGroups.length === 0) {
      setSelectedTag('');
      return;
    }
    if (!selectedTag || !orderedGroups.some((group) => group.tag === selectedTag)) {
      setSelectedTag(orderedGroups[0].tag);
    }
  }, [orderedGroups, selectedTag]);

  const selectedGroup = orderedGroups.find((group) => group.tag === selectedTag) || orderedGroups[0] || null;
  const drawTimezone = resolveLotteryTimezone(drawTimeConfig);
  const currentOpenDisplayTime = buildTimezoneAwareTime(latest?.prev?.opentime, drawTimezone)?.format('YYYY-MM-DD HH:mm') || '--';
  const nextOpenDisplayTime = buildTimezoneAwareTime(latest?.next?.opentime, drawTimezone)?.format('YYYY-MM-DD HH:mm') || '--';
  const currentGame = GAMES.find((item) => item.code === game) || GAMES[0];
  const siteName = resolveSiteName(siteSettings) || '彩票网站';
  const siteShortName = resolveSiteShortName(siteSettings) || siteName;
  const siteDescription = siteSettings?.site_description?.trim() || '彩势情报';
  const siteNotice = siteSettings?.site_notice?.trim() || '';
  const siteLogo = resolveUploadUrl(siteSettings?.site_logo);
  const heroTitle = selectedGroup ? compactPlayTitle(selectedGroup.title, game) : '热门资料';
  const issueLabel = latest?.prev?.period ? shortPeriod(latest.prev.period) : '--';
  const showInlineMiddleImageAd = game === 'xglhc' || game === 'amlhc';
  const showBottomMiddleImageAd = game === 'amssc' || game === 'xamlhc';
  const [revealedDrawBallCount, setRevealedDrawBallCount] = useState(0);
  const [drawAnimationPhase, setDrawAnimationPhaseRaw] = useState<'idle' | 'syncing' | 'revealing'>('idle');
  const setDrawAnimationPhase = useCallback((phase: 'idle' | 'syncing' | 'revealing') => {
    drawAnimationPhaseRef.current = phase;
    setDrawAnimationPhaseRaw(phase);
  }, []);
  const revealIntervalRef = useRef<number | null>(null);
  const pendingLatestRef = useRef<LatestDrawResponse | null>(null);
  const drawAnimationPhaseRef = useRef<'idle' | 'syncing' | 'revealing'>('idle');
  // 开奖周期状态：normal=可触发动画；awaiting_result=动画播完，等后端 is_drawing=false 再刷新
  const drawCycleRef = useRef<'normal' | 'awaiting_result'>('normal');
  // syncing 阶段开始时间戳，用于"至少同步 10 秒"的视觉节奏
  const syncingStartedAtRef = useRef<number>(0);
  // 触发动画时的 prev.period（基线），用于判断"号码是否已采集到新一期"
  const animateBaselinePeriodRef = useRef<string>('');
  const selectedGroupHitEffectMode = selectedGroup
    ? playHitEffectModeByTag[selectedGroup.tag] || playHitEffectModeByTag[normalizePlayTag(selectedGroup.tag)]
    : undefined;
  const { normalBalls, specialBalls } = splitDrawBalls(
    latest?.prev?.format || [],
    latest?.prev?.regular_count,
    latest?.prev?.special_count,
  );
  const drawBallItems = useMemo(
    () => [
      ...normalBalls.map((ball) => ({ ball, isSpecial: false })),
      ...specialBalls.map((ball) => ({ ball, isSpecial: true })),
    ],
    [normalBalls, specialBalls],
  );
  const drawSyncChars = useMemo(() => Array.from('正在同步开奖中'), []);

  const clearRevealTimers = useCallback(() => {
    if (revealIntervalRef.current !== null) {
      window.clearInterval(revealIntervalRef.current);
      revealIntervalRef.current = null;
    }
  }, []);

  // 进入 syncing：显示"正在同步开奖中"，等号码到位
  const enterSyncing = useCallback((baselinePeriod: string) => {
    clearRevealTimers();
    syncingStartedAtRef.current = Date.now();
    animateBaselinePeriodRef.current = baselinePeriod;
    setRevealedDrawBallCount(0);
    setDrawAnimationPhase('syncing');
  }, [clearRevealTimers, setDrawAnimationPhase]);

  // 检查是否可从 syncing 推进到 revealing：满 10s + 拿到了新号码
  const tryEnterRevealing = useCallback(() => {
    if (drawAnimationPhaseRef.current !== 'syncing') {
      return;
    }
    const pending = pendingLatestRef.current;
    if (!pending) {
      return;
    }
    const newPeriod = String(pending.prev?.period || '');
    if (newPeriod === '' || newPeriod === animateBaselinePeriodRef.current) {
      return; // 号码还没到位
    }
    if ((pending.prev?.format?.length || 0) === 0) {
      return;
    }
    const elapsed = Date.now() - syncingStartedAtRef.current;
    if (elapsed < 10000) {
      return; // 同步动画至少播 10s
    }
    // 推进到 revealing
    pendingLatestRef.current = null;
    setLatest(pending);
    setDrawAnimationPhase('revealing');
    setRevealedDrawBallCount(1);
    const total = pending.prev?.format?.length || 7;
    revealIntervalRef.current = window.setInterval(() => {
      setRevealedDrawBallCount((current) => {
        const nextCount = current + 1;
        if (nextCount >= total) {
          if (revealIntervalRef.current !== null) {
            window.clearInterval(revealIntervalRef.current);
            revealIntervalRef.current = null;
          }
          setDrawAnimationPhase('idle');
          // 揭示完成 → 等后端开奖结算（is_drawing=false）后再刷新一次
          drawCycleRef.current = 'awaiting_result';
        }
        return nextCount;
      });
    }, 7000);
  }, [setDrawAnimationPhase]);

  const tryEnterRevealingRef = useRef(tryEnterRevealing);
  tryEnterRevealingRef.current = tryEnterRevealing;
  const enterSyncingRef = useRef(enterSyncing);
  enterSyncingRef.current = enterSyncing;

  const visibleDrawBallCount = useMemo(() => {
    if (drawAnimationPhase === 'syncing') {
      return 0;
    }
    if (drawAnimationPhase === 'revealing') {
      return Math.max(0, Math.min(revealedDrawBallCount, drawBallItems.length));
    }
    return drawBallItems.length;
  }, [drawAnimationPhase, drawBallItems.length, revealedDrawBallCount]);

  const drawDisplaySlots = useMemo(() => {
    if (drawAnimationPhase === 'idle') {
      return drawBallItems.map((item) => ({
        type: 'ball' as const,
        ball: item.ball,
        isSpecial: item.isSpecial,
        syncChar: '',
      }));
    }
    const slotCount = Math.max(drawBallItems.length, drawSyncChars.length);
    return Array.from({ length: slotCount }, (_, index) => {
      if (index < visibleDrawBallCount && index < drawBallItems.length) {
        return {
          type: 'ball' as const,
          ball: drawBallItems[index].ball,
          isSpecial: drawBallItems[index].isSpecial,
          syncChar: '',
        };
      }
      return {
        type: 'char' as const,
        ball: null,
        isSpecial: drawBallItems[index]?.isSpecial || false,
        syncChar: drawSyncChars[index] || '',
      };
    });
  }, [drawAnimationPhase, drawBallItems, drawSyncChars, visibleDrawBallCount]);

  useEffect(() => {
    if (!latest?.next?.opentime) {
      setCountdown(drawAnimationPhase !== 'idle' ? '开奖中' : '等待开奖');
      return;
    }

    let timer = 0;
    const tick = () => {
      const target = buildTimezoneAwareTime(latest.next.opentime, drawTimezone);
      if (!target) {
        setCountdown('');
        return;
      }
      if (drawAnimationPhase !== 'idle') {
        setCountdown('开奖中');
        return;
      }
      const diff = target.diff(dayjs().tz(drawTimezone), 'second');
      if (diff <= 0) {
        // 倒计时归零本身不代表"正在开奖"——只有后端 is_drawing=true 才是真的在开奖窗口里
        setCountdown(latest.is_drawing ? '开奖中' : '等待开奖');
        return;
      }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      const pad = (value: number) => String(value).padStart(2, '0');
      setCountdown(d > 0 ? `${d}天 ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`);
    };

    tick();
    timer = window.setInterval(tick, 1000);
    return () => {
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [drawAnimationPhase, drawTimezone, latest?.next?.opentime, latest?.is_drawing]);

  useEffect(() => clearRevealTimers, [clearRevealTimers]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refreshLatestDraw();
    }, 5000);
    return () => {
      window.clearInterval(timer);
    };
  }, [refreshLatestDraw]);

  useEffect(() => {
    clearRevealTimers();
    setLatest(null);
    setDrawTimeConfig(null);
    setPlayTypes([]);
    setGroups([]);
    setCountdown('');
    setRevealedDrawBallCount(0);
    setDrawAnimationPhase('idle');
    pendingLatestRef.current = null;
  }, [clearRevealTimers, game]);

  useEffect(() => {
    applySiteMeta(siteSettings, {
      title: `${siteName} - ${currentGame.name}`,
      appName: siteName,
      appShortName: siteShortName,
      fallbackDescription: `${siteName} ${currentGame.name} ${siteDescription}，提供实时开奖、热门资料和历史走势。`,
      fallbackKeywords: `${siteName},${currentGame.name},${siteShortName},开奖,走势,历史记录`,
    });
  }, [currentGame.name, siteDescription, siteName, siteSettings, siteShortName]);

  return (
    <div className="home2-page">
      <div className="home2-topbar">
        <div className="brand-pill">
          {siteLogo ? <img className="brand-pill__logo" src={siteLogo} alt={siteName} /> : null}
          <div className="brand-pill__text">
            <div className="brand-pill__title">{siteName}</div>
            <div className="brand-pill__subtitle">{siteDescription}</div>
          </div>
        </div>
        <button type="button" className="topbar-action" onClick={() => navigate(`/history?game=${game}`)}>
          开奖记录
        </button>
      </div>

      <div className="game-strip">
        {GAMES.map((item) => (
          <button
            key={item.code}
            type="button"
            className={`game-strip-item ${item.code === game ? 'is-active' : ''}`}
            onClick={() => setSearchParams({ game: item.code }, { replace: true })}
          >
            {item.shortName}
          </button>
        ))}
      </div>

      <AdCarousel position="home_top" />

      <section className="issue-board">
        <div className="issue-board-head">
          <div>
            <div className="issue-board-kicker">{currentGame.name}</div>
            <div className="issue-board-period">{latest?.prev?.period ? formatPeriod(latest.prev.period) : '等待开奖'}</div>
          </div>
          <div className="issue-board-meta">
            <span className="meta-chip">{countdown || '同步中'}</span>
            <span className="meta-text">下期 {latest?.next?.period || '--'}</span>
          </div>
        </div>
        <div className="issue-board-balls">
          {(drawBallItems.length > 0 || drawAnimationPhase !== 'idle') ? drawDisplaySlots.map((slot, index) => (
            slot.type === 'ball' && slot.ball ? (
              <LotteryBall
                key={`${latest?.prev?.period || 'draw'}-${slot.ball.title}-${index}`}
                ball={slot.ball}
                size="sm"
                showSx
                isSpecial={slot.isSpecial}
                reveal={drawAnimationPhase === 'revealing'}
              />
            ) : (
              <div
                key={`${latest?.prev?.period || 'draw'}-sync-${index}`}
                className={`draw-sync-slot ${slot.isSpecial ? 'is-special' : ''}`}
                aria-hidden="true"
              >
                <div className="draw-sync-char">{slot.syncChar}</div>
                <div className="draw-sync-subline" />
              </div>
            )
          )) : <div className="ball-placeholder">当前暂无可展示开奖号</div>}
        </div>
        <div className="issue-board-foot">
          <span>当前期号 {issueLabel}</span>
          <span>本期开奖 {currentOpenDisplayTime}</span>
          <span>下期开奖 {nextOpenDisplayTime}</span>
        </div>
      </section>

      <section className="menu-board">
        <div className="section-title">热门资料</div>
        <div className="menu-grid">
          {orderedGroups.map((group) => {
            const isActive = group.tag === selectedGroup?.tag;
            return (
              <button
                key={group.tag}
                type="button"
                className={`menu-chip ${isActive ? 'is-active' : ''}`}
                onClick={() => setSelectedTag(group.tag)}
              >
                {compactPlayTitle(group.title, game)}
              </button>
            );
          })}
        </div>
      </section>

      <AdCarousel position="home_middle" />

      {/* 中部图片广告位 - 港六合和澳六合显示在中部 */}
      {showInlineMiddleImageAd && (
        <AdCarousel position="home_middle_image" displayMode="stack" />
      )}

      <section className="feature-board">
        <div className="feature-banner">
          <div className="feature-tag">{siteShortName}</div>
          <h1 className="feature-title">「{heroTitle}」</h1>
          <div className="feature-subtitle">
            {selectedGroup?.rule ? `${selectedGroup.rule} · ` : ''}
            {siteNotice || `参考近 ${selectedGroup?.records?.length || 0} 期走势`}
          </div>
        </div>
        <PlayRuleLegend groups={selectedGroup?.rule_details} title="玩法规则对照" />

        <div className="record-list">
          {loading && (
            <div className="records-loading">
              <DotLoading />
            </div>
          )}
          {!loading && selectedGroup?.records?.length ? (
            selectedGroup.records.map((record) => {
              const visualMode = record.visual_payload?.hit_effect_mode || selectedGroupHitEffectMode;
              const visualRecord = hasVisualItems(record);
              return (
                <div key={record.id} className={`record-row${visualRecord ? ' has-visual' : ''}`}>
                  <div className="record-period">{shortPeriod(record.period)}</div>
                  <div className="record-body">
                    <div className={`record-prediction${visualRecord ? ' is-visual' : ''}`}>
                      {visualRecord ? (
                        <ImageBindingSceneCard
                          items={record.visual_payload!.items}
                          hitEffectMode={visualMode}
                          backgroundImage={record.visual_payload?.background_image}
                          sceneWidth={record.visual_payload?.scene_width}
                          sceneHeight={record.visual_payload?.scene_height}
                          period={shortPeriod(record.period)}
                          suc={record.suc}
                          openTitle={record.open_title}
                        />
                      ) : (
                        renderPredictionTitle(
                          record.title,
                          selectedGroup.tag,
                          record.open_title,
                          record.effect_values,
                          selectedGroupHitEffectMode,
                        )
                      )}
                    </div>
                    <div className="record-result">
                      <span className="record-result-label">开:</span>
                      <span className={`record-result-value${isPendingRecord(record) ? ' is-pending' : ''}`}>
                        {openResultText(record)}
                      </span>
                      {record.open_scope ? <span className="record-result-scope">{record.open_scope}</span> : null}
                    </div>
                  </div>
                  <div className="record-side">{renderJudge(record)}</div>
                </div>
              );
            })
          ) : !loading ? (
            <div className="records-empty">当前玩法暂无数据</div>
          ) : null}
        </div>
      </section>

      <div className={`home2-actions${showBottomMiddleImageAd ? ' home2-actions--with-bottom-ad' : ''}`}>
        <button type="button" className="action-button ghost" onClick={() => void fetchData()}>
          刷新数据
        </button>
        <button type="button" className="action-button" onClick={() => navigate(`/history?game=${game}`)}>
          查看历史开奖
        </button>
      </div>
      {showBottomMiddleImageAd && (
        <AdCarousel position="home_middle_image" displayMode="stack" />
      )}
    </div>
  );
};

export default Home;
